import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { formatCurrency } from '@/utils/common/formatting';
import { getIsOverdue, getInCredit } from '@/features/payments/common/helpers';

interface PaymentSummaryCardProps {
  balanceDue: number | null | undefined;
  isLoading?: boolean;
  onPress: () => void;
  onMakePayment: () => void;
}

export const PaymentSummaryCard: React.FC<PaymentSummaryCardProps> = ({
  balanceDue,
  isLoading = false,
  onPress,
  onMakePayment,
}) => {
  const balance = Number(balanceDue || 0);
  const isOverdue = getIsOverdue(balance);
  const inCredit = getInCredit(balance);
  const hasBalance = balance > 0;
  const isZeroBalance = balance === 0;

  // Get status text based on balance state
  const getStatusText = (): string => {
    if (isOverdue) {
      return 'Past due balance';
    }
    if (inCredit) {
      return 'Account credit';
    }
    if (isZeroBalance) {
      return 'No balance due';
    }
    return 'Balance due';
  };

  // Get balance display text
  const getBalanceDisplay = (): string => {
    if (inCredit) {
      return `(${formatCurrency(Math.abs(balance))})`;
    }
    return formatCurrency(balance);
  };

  // Get balance text color
  const getBalanceColor = () => {
    if (isOverdue) return styles.balanceOverdue;
    if (inCredit) return styles.balanceCredit;
    return styles.balanceNormal;
  };

  // Get icon background color based on state
  const getIconBackground = () => {
    if (isOverdue) return { backgroundColor: 'rgba(239, 68, 68, 0.15)' }; // Red tint
    if (inCredit || isZeroBalance) return { backgroundColor: 'rgba(102, 206, 102, 0.15)' }; // Green tint
    return { backgroundColor: MARKETPLACE_COLORS.white10Percent }; // Default
  };

  // Get icon color based on state
  const getIconColor = () => {
    if (isOverdue) return MARKETPLACE_COLORS.dangerRed;
    if (inCredit || isZeroBalance) return '#66CE66'; // Success green
    return MARKETPLACE_COLORS.accentBlueSolid;
  };

  return (
    <TouchableOpacity
      style={styles.card}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Payment summary: ${getStatusText()}, ${getBalanceDisplay()}`}
    >
      <View style={styles.content}>
        {/* Icon */}
        <View style={[styles.icon, getIconBackground()]}>
          <Icon
            name="card-outline"
            size={20}
            color={getIconColor()}
          />
        </View>

        {/* Text Content */}
        <View style={styles.textContent}>
          <Text style={styles.title}>Payments</Text>
          
          {isLoading ? (
            <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textMuted} style={styles.loader} />
          ) : (
            <>
              <Text style={[styles.balance, getBalanceColor()]}>
                {getBalanceDisplay()}
              </Text>
              <Text style={styles.status}>{getStatusText()}</Text>
            </>
          )}
        </View>

        {/* Chevron */}
        <Icon
          name="chevron-forward"
          size={24}
          color={MARKETPLACE_COLORS.inputBorder}
          style={styles.chevron}
        />
      </View>

      {/* Make Payment Button */}
      {hasBalance && !isLoading && (
        <TouchableOpacity
          style={[styles.button, isOverdue && styles.buttonOverdue]}
          onPress={onMakePayment}
          accessibilityRole="button"
          accessibilityLabel="Make a payment"
        >
          <Text style={[styles.buttonText, isOverdue && styles.buttonTextOverdue]}>Make a payment</Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  icon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  textContent: {
    flex: 1,
    paddingTop: 2,
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    marginBottom: 2,
  },
  balance: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 2,
  },
  balanceNormal: {
    color: MARKETPLACE_COLORS.textPrimary,
  },
  balanceOverdue: {
    color: MARKETPLACE_COLORS.dangerRed,
  },
  balanceCredit: {
    color: '#66CE66',
  },
  status: {
    fontSize: 13,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textMuted,
  },
  chevron: {
    marginTop: 8,
  },
  loader: {
    alignSelf: 'flex-start',
    marginVertical: 8,
  },
  button: {
    backgroundColor: MARKETPLACE_COLORS.accentBlueSolid,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignSelf: 'flex-start',
    marginTop: 12,
    marginLeft: 52, // Align with text content (40 icon + 12 margin)
  },
  buttonOverdue: {
    backgroundColor: MARKETPLACE_COLORS.accentRed,
  },
  buttonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
  },
  buttonTextOverdue: {
    color: MARKETPLACE_COLORS.black,
  },
});
